import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * DonationsApiService (Angular 18) — donation amounts, configs, custom fields.
 *
 * Replaces apps/b2b-ui/common/services/api/donationsApi/donationsApi.service.js.
 *
 * Top-level CRUD takes a `type` discriminator (e.g. 'amount', 'aspect') that
 * is appended to the URL: `/v2/api/donation/<type>[/<id>]`.
 *
 * `theme()` reads a static asset (preserved from legacy).
 */
const DONATION_BASE_V2 = '/v2/api/donation';
const DONATION_CONFIGS_BASE_V2 = '/v2/api/donation/configs';
const DONATION_CUSTOM_FIELDS_BASE_V2 = '/v2/api/donation/customfields';

interface DonationPayload {
  id?: number | string;
  name: string;
  description?: string;
  isDefault?: boolean;
  online?: boolean;
  csr?: boolean;
  pos?: boolean;
  insideSalesIndividual?: boolean;
}

interface DonationConfigPayload {
  id?: number | string;
  aspect: string;
  description?: string;
  online?: boolean;
  csr?: boolean;
  pos?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DonationsApiService {
  private readonly http = inject(HttpClient);

  theme(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>('/assets/data/donate.json'));
  }

  list(type: string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${DONATION_BASE_V2}/${type}`, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  create(type: string, props: DonationPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${DONATION_BASE_V2}/${type}`, {
        name: props.name,
        description: props.description,
        isDefault: props.isDefault,
        online: props.online,
        csr: props.csr,
        pos: props.pos,
        insideSalesIndividual: props.insideSalesIndividual,
      }),
    );
  }

  update(type: string, props: DonationPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${DONATION_BASE_V2}/${type}`, {
        id: props.id,
        name: props.name,
        description: props.description,
        isDefault: props.isDefault,
        online: props.online,
        csr: props.csr,
        pos: props.pos,
        insideSalesIndividual: props.insideSalesIndividual,
      }),
    );
  }

  remove(type: string, id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${DONATION_BASE_V2}/${type}/${id}`));
  }

  readonly configs = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(DONATION_CONFIGS_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    create: (props: DonationConfigPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(DONATION_CONFIGS_BASE_V2, {
          aspect: props.aspect,
          description: props.description,
          online: props.online,
          csr: props.csr,
          pos: props.pos,
        }),
      ),

    update: (props: DonationConfigPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(DONATION_CONFIGS_BASE_V2, {
          id: props.id,
          aspect: props.aspect,
          description: props.description,
          online: props.online,
          csr: props.csr,
          pos: props.pos,
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${DONATION_CONFIGS_BASE_V2}/${id}`)),
  };

  readonly customFields = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(DONATION_CUSTOM_FIELDS_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    create: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(DONATION_CUSTOM_FIELDS_BASE_V2, props)),

    update: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(DONATION_CUSTOM_FIELDS_BASE_V2, props)),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${DONATION_CUSTOM_FIELDS_BASE_V2}/${id}`)),
  };
}
